package com.havn.app.features.leads.data

import androidx.paging.Pager
import androidx.paging.PagingConfig
import androidx.paging.PagingData
import androidx.paging.PagingSource
import androidx.paging.PagingState
import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.HavnMediaBucket
import com.havn.app.core.media.havnDisplayPhotoSource
import com.havn.app.core.media.havnPublicStorageSource
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.domain.LeadFollowUp
import com.havn.app.features.leads.domain.LeadOpenHouseSource
import com.havn.app.features.leads.domain.LeadPropertySource
import com.havn.app.features.leads.domain.LeadRepository
import com.havn.app.features.openhouses.domain.LeadTemperature
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Clock

class SupabaseLeadRepository(
    private val client: SupabaseClient
) : LeadRepository {
    private val pageCacheMutex = Mutex()
    private val pageCache = mutableMapOf<SearchPageCacheKey, LeadPageCacheEntry>()

    override suspend fun leadsForAgent(agentId: String, agencyId: String?): Result<List<Lead>> {
        return leadPage(agentId, agencyId, query = "", offset = 0, limit = LEAD_PAGE_SIZE)
    }

    override suspend fun leadPage(
        agentId: String,
        agencyId: String?,
        query: String,
        offset: Int,
        limit: Int
    ): Result<List<Lead>> {
        val normalizedQuery = query.trim()
        val cacheKey = SearchPageCacheKey(agentId, agencyId, normalizedQuery, offset, limit)
        return pageCacheMutex.withLock {
            val nowMs = Clock.System.now().toEpochMilliseconds()
            pageCache[cacheKey]
                ?.takeIf { nowMs - it.savedAtMs <= PAGE_CACHE_TTL_MS }
                ?.let { return@withLock Result.success(it.value) }

            val result = runCatching {
                HavnLogger.debug("Leads", "Loading leads for agent $agentId")
                val rows = recentLeadRows(agencyId = agencyId, query = normalizedQuery, offset = offset, limit = limit)
                    .decodeList<LeadRow>()
                hydrateMissingListAvatars(rows).map { it.toListDomain() }
            }.onFailure {
                HavnLogger.error("Leads", "Failed to load leads for $agentId", it)
            }
            result.onSuccess { pageCache[cacheKey] = LeadPageCacheEntry(nowMs, it) }
            result
        }
    }

    override fun pagedLeadsForAgent(agentId: String, agencyId: String?): Flow<PagingData<Lead>> {
        return Pager(
            config = PagingConfig(
                pageSize = LEAD_PAGE_SIZE,
                initialLoadSize = LEAD_PAGE_SIZE,
                prefetchDistance = 10,
                enablePlaceholders = false
            ),
            pagingSourceFactory = {
                LeadPagingSource(
                    repository = this,
                    agentId = agentId,
                    agencyId = agencyId
                )
            }
        ).flow
    }

    override suspend fun leadDetail(agentId: String, leadId: String): Result<Lead> {
        return runCatching {
            HavnLogger.debug("Leads", "Loading lead detail $leadId")
            client.from(LEADS)
                .select {
                    filter {
                        eq("id", leadId)
                    }
                    limit(1)
                }
                .decodeSingle<LeadRow>()
                .toDomain(agentId)
        }.onFailure {
            HavnLogger.error("Leads", "Failed to load lead $leadId", it)
        }
    }

    override suspend fun updateLeadTemperature(
        agentId: String,
        leadId: String,
        temperature: LeadTemperature
    ): Result<Unit> {
        return runCatching {
            HavnLogger.info("Leads", "Updating lead $leadId temperature to ${temperature.databaseValue}")
            client.postgrest.rpc(
                function = "set_lead_temperature",
                parameters = SetLeadTemperatureParams(
                    leadId = leadId,
                    temperature = temperature.databaseValue.lowercase()
                )
            ).decodeAs<LeadIdRow>()
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("Leads", "Failed to update temperature for $leadId", it)
        }
    }

    override suspend fun updateLeadNotes(
        agentId: String,
        leadId: String,
        notes: String
    ): Result<Lead> {
        return runCatching {
            HavnLogger.info("Leads", "Updating private notes for lead $leadId")
            client.from(LEADS).update(LeadNotesUpdateRow(notes.trim().takeIf { it.isNotBlank() })) {
                filter {
                    eq("id", leadId)
                }
                select()
            }.decodeSingle<LeadIdRow>()

            clearPageCache()
            leadDetail(agentId, leadId).getOrThrow()
        }.onFailure {
            HavnLogger.error("Leads", "Failed to update notes for $leadId", it)
        }
    }

    private suspend fun LeadRow.toDomain(agentId: String): Lead {
        val clientRow = clientFor(clientId)
        val visitorRow = visitorId?.let { visitorFor(agentId, it) }
        val openingClientRow = openingClientFor(openingId, clientId)
        val openHouse = openHouseFor(agentId, openingId)
        val property = propertyFor(propertyId)
        return Lead(
            id = id,
            visitorId = visitorId,
            openHouseId = openingId,
            propertyId = property.id,
            fullName = clientRow.fullName,
            email = clientRow.email,
            phone = clientRow.phone,
            avatarDataUrl = clientRow.displayAvatarUrl,
            temperature = LeadTemperature.fromDatabase(temperature),
            intentScore = intentScore,
            notes = notes ?: visitorRow?.notes,
            checkedInAt = visitorRow?.checkedInAt ?: openingClientRow?.checkedInAt ?: createdAt ?: updatedAt ?: openHouse.startsAt,
            createdAt = createdAt,
            updatedAt = updatedAt,
            sourceProperty = property,
            sourceOpenHouse = openHouse.toDomain(),
            followUps = followUpsFor(agentId, visitorId)
        )
    }

    private suspend fun clientFor(clientId: String): LeadClientRow {
        return client.from(CLIENTS)
            .select(Columns.list("id", "full_name", "email", "phone", "avatar_url", "avatar_storage_path")) {
                filter { eq("id", clientId) }
                limit(1)
            }
            .decodeSingle()
    }

    private suspend fun visitorFor(agentId: String, visitorId: String): VisitorRow? {
        return client.from(VISITORS)
            .select(Columns.list("id", "checked_in_at", "notes")) {
                filter {
                    eq("id", visitorId)
                }
                limit(1)
            }
            .decodeSingleOrNull()
    }

    private suspend fun openingClientFor(openingId: String, clientId: String): OpeningClientRow? {
        return client.from(OPENING_CLIENTS)
            .select(Columns.list("id", "checked_in_at")) {
                filter {
                    eq("opening_id", openingId)
                    eq("contact_id", clientId)
                }
                limit(1)
            }
            .decodeSingleOrNull()
    }

    private suspend fun openHouseFor(agentId: String, openHouseId: String): LeadOpenHouseRow {
        return client.from(OPEN_HOUSES)
            .select(Columns.list("id", "property_id", "title", "starts_at", "ends_at", "status")) {
                filter {
                    eq("id", openHouseId)
                }
                limit(1)
            }
            .decodeSingle()
    }

    private suspend fun propertyFor(propertyId: String): LeadPropertySource {
        val property = client.from(PROPERTIES)
            .select(Columns.list("id", "title", "address_line_1", "city", "region")) {
                filter { eq("id", propertyId) }
                limit(1)
            }
            .decodeSingle<LeadPropertyRow>()
            .toDomain()
        val photos = propertyPhotoDataUrls(propertyId)
        return property.copy(photoDataUrls = photos)
    }

    private suspend fun propertyPhotoDataUrls(propertyId: String): List<String> {
        return client.from(PROPERTY_PHOTOS)
            .select(Columns.list("id", "storage_path", "thumbnail_url", "thumbnail_width", "sort_order", "created_at")) {
                filter { eq("property_id", propertyId) }
                order("sort_order", Order.ASCENDING)
                order("created_at", Order.ASCENDING)
            }
            .decodeList<LeadPropertyPhotoRow>()
            .mapNotNull { it.displayDataUrl }
    }

    private suspend fun followUpsFor(agentId: String, visitorId: String?): List<LeadFollowUp> {
        if (visitorId == null) return emptyList()
        return client.from(FOLLOW_UPS)
            .select {
                filter {
                    eq("visitor_id", visitorId)
                }
                order("created_at", Order.DESCENDING)
                limit(LEAD_PAGE_SIZE.toLong())
            }
            .decodeList<LeadFollowUpRow>()
            .map { it.toDomain() }
    }

    internal suspend fun leadPage(
        agentId: String,
        agencyId: String?,
        offset: Int,
        limit: Int
    ): List<Lead> = leadPage(agentId, agencyId, query = "", offset = offset, limit = limit).getOrThrow()

    private suspend fun recentLeadRows(agencyId: String?, query: String, offset: Int, limit: Int) =
        client.postgrest.rpc(
            function = LEAD_SEARCH_RPC,
            parameters = SearchPageParams(
                agencyId = agencyId,
                query = query.trim().takeIf { it.isNotBlank() },
                limit = limit,
                offset = offset
            )
        )

    private suspend fun hydrateMissingListAvatars(rows: List<LeadRow>): List<LeadRow> {
        val missingAvatarContactIds = rows
            .filter { it.contactAvatarDataUrl.isNullOrBlank() }
            .map { it.clientId }
            .distinct()
        if (missingAvatarContactIds.isEmpty()) return rows

        val avatarsByContactId = clientsFor(missingAvatarContactIds)
        if (avatarsByContactId.isEmpty()) return rows

        return rows.map { row ->
            if (!row.contactAvatarDataUrl.isNullOrBlank()) {
                row
            } else {
                row.copy(contactAvatarDataUrl = avatarsByContactId[row.clientId]?.displayAvatarUrl)
            }
        }
    }

    private suspend fun clearPageCache() {
        pageCacheMutex.withLock { pageCache.clear() }
    }

    private suspend fun List<LeadRow>.toLeadList(agentId: String): List<Lead> {
        if (isEmpty()) return emptyList()

        val clients = clientsFor(map { it.clientId }.distinct())
        val visitors = visitorsFor(mapNotNull { it.visitorId }.distinct())
        val openingContacts = openingClientsFor(
            openingIds = map { it.openingId }.distinct(),
            clientIds = map { it.clientId }.distinct()
        )
        val openHouses = openHousesFor(map { it.openingId }.distinct())
        val properties = propertiesFor(map { it.propertyId }.distinct())
        val followUps = followUpsForVisitors(mapNotNull { it.visitorId }.distinct())

        return map { row ->
            val clientRow = clients.getValue(row.clientId)
            val visitorRow = row.visitorId?.let { visitors[it] }
            val openingClientRow = openingContacts[row.openingId to row.clientId]
            val openHouse = openHouses.getValue(row.openingId)
            val property = properties.getValue(row.propertyId)
            Lead(
                id = row.id,
                visitorId = row.visitorId,
                openHouseId = row.openingId,
                propertyId = property.id,
                fullName = clientRow.fullName,
                email = clientRow.email,
                phone = clientRow.phone,
                avatarDataUrl = clientRow.displayAvatarUrl,
                temperature = LeadTemperature.fromDatabase(row.temperature),
                intentScore = row.intentScore,
                notes = row.notes ?: visitorRow?.notes,
                checkedInAt = visitorRow?.checkedInAt ?: openingClientRow?.checkedInAt ?: row.createdAt ?: row.updatedAt ?: openHouse.startsAt,
                createdAt = row.createdAt,
                updatedAt = row.updatedAt,
                sourceProperty = property,
                sourceOpenHouse = openHouse.toDomain(),
                followUps = row.visitorId?.let { followUps[it] }.orEmpty()
            )
        }
    }

    private suspend fun clientsFor(clientIds: List<String>): Map<String, LeadClientRow> {
        if (clientIds.isEmpty()) return emptyMap()
        return client.from(CLIENTS)
            .select(Columns.list("id", "full_name", "email", "phone", "avatar_url", "avatar_storage_path")) {
                filter { isIn("id", clientIds) }
            }
            .decodeList<LeadClientRow>()
            .associateBy { it.id }
    }

    private suspend fun visitorsFor(visitorIds: List<String>): Map<String, VisitorRow> {
        if (visitorIds.isEmpty()) return emptyMap()
        return client.from(VISITORS)
            .select(Columns.list("id", "checked_in_at", "notes")) {
                filter { isIn("id", visitorIds) }
            }
            .decodeList<VisitorRow>()
            .associateBy { it.id }
    }

    private suspend fun openingClientsFor(
        openingIds: List<String>,
        clientIds: List<String>
    ): Map<Pair<String, String>, OpeningClientRow> {
        if (openingIds.isEmpty() || clientIds.isEmpty()) return emptyMap()
        return client.from(OPENING_CLIENTS)
            .select(Columns.list("id", "opening_id", "contact_id", "checked_in_at")) {
                filter {
                    isIn("opening_id", openingIds)
                    isIn("contact_id", clientIds)
                }
            }
            .decodeList<OpeningClientRow>()
            .associateBy { it.openingId to it.clientId }
    }

    private suspend fun openHousesFor(openHouseIds: List<String>): Map<String, LeadOpenHouseRow> {
        if (openHouseIds.isEmpty()) return emptyMap()
        return client.from(OPEN_HOUSES)
            .select(Columns.list("id", "property_id", "title", "starts_at", "ends_at", "status")) {
                filter { isIn("id", openHouseIds) }
            }
            .decodeList<LeadOpenHouseRow>()
            .associateBy { it.id }
    }

    private suspend fun propertiesFor(propertyIds: List<String>): Map<String, LeadPropertySource> {
        if (propertyIds.isEmpty()) return emptyMap()
        return client.from(PROPERTIES)
            .select(Columns.list("id", "title", "address_line_1", "city", "region")) {
                filter { isIn("id", propertyIds) }
            }
            .decodeList<LeadPropertyRow>()
            .associateBy({ it.id }, { it.toDomain() })
    }

    private suspend fun followUpsForVisitors(visitorIds: List<String>): Map<String, List<LeadFollowUp>> {
        if (visitorIds.isEmpty()) return emptyMap()
        return client.from(FOLLOW_UPS)
            .select {
                filter { isIn("visitor_id", visitorIds) }
                order("created_at", Order.DESCENDING)
                limit((visitorIds.size * 3).coerceAtMost(150).toLong())
            }
            .decodeList<LeadFollowUpRow>()
            .groupBy(
                keySelector = { it.visitorId },
                valueTransform = { it.toDomain() }
            )
    }

    private companion object {
        const val LEAD_PAGE_SIZE = 50
        const val PAGE_CACHE_TTL_MS = 15_000L
        const val LEAD_SEARCH_RPC = "search_leads_for_current_user"
        const val VISITORS = "visitors"
        const val LEADS = "leads"
        const val CLIENTS = "contacts"
        const val OPENING_CLIENTS = "opening_contacts"
        const val OPEN_HOUSES = "open_houses"
        const val PROPERTIES = "properties"
        const val PROPERTY_PHOTOS = "property_photos"
        const val FOLLOW_UPS = "follow_ups"
    }
}

private class LeadPagingSource(
    private val repository: SupabaseLeadRepository,
    private val agentId: String,
    private val agencyId: String?
) : PagingSource<Int, Lead>() {
    override suspend fun load(params: LoadParams<Int>): LoadResult<Int, Lead> {
        val offset = params.key ?: 0
        return try {
            val leads = repository.leadPage(
                agentId = agentId,
                agencyId = agencyId,
                offset = offset,
                limit = params.loadSize
            )
            LoadResult.Page(
                data = leads,
                prevKey = (offset - params.loadSize).takeIf { it >= 0 },
                nextKey = (offset + leads.size).takeIf { leads.size == params.loadSize }
            )
        } catch (error: Throwable) {
            HavnLogger.error("Leads", "Failed to load paged lead rows", error)
            LoadResult.Error(error)
        }
    }

    override fun getRefreshKey(state: PagingState<Int, Lead>): Int? {
        return state.anchorPosition?.let { anchor ->
            val page = state.closestPageToPosition(anchor)
            page?.prevKey?.plus(state.config.pageSize) ?: page?.nextKey?.minus(state.config.pageSize)
        }
    }
}

@Serializable
private data class LeadIdRow(val id: String)

@Serializable
private data class LeadRow(
    val id: String,
    @SerialName("property_id") val propertyId: String,
    @SerialName("opening_id") val openingId: String,
    @SerialName("contact_id") val clientId: String,
    @SerialName("visitor_id") val visitorId: String? = null,
    val temperature: String? = null,
    @SerialName("intent_score") val intentScore: Int? = null,
    val notes: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null,
    @SerialName("contact_full_name") val contactFullName: String? = null,
    @SerialName("contact_email") val contactEmail: String? = null,
    @SerialName("contact_phone") val contactPhone: String? = null,
    @SerialName("contact_avatar_url") val contactAvatarDataUrl: String? = null,
    @SerialName("contact_avatar_storage_path") val contactAvatarStoragePath: String? = null,
    @SerialName("visitor_checked_in_at") val visitorCheckedInAt: String? = null,
    @SerialName("visitor_notes") val visitorNotes: String? = null,
    @SerialName("opening_contact_checked_in_at") val openingContactCheckedInAt: String? = null,
    @SerialName("open_house_title") val openHouseTitle: String? = null,
    @SerialName("open_house_starts_at") val openHouseStartsAt: String? = null,
    @SerialName("open_house_ends_at") val openHouseEndsAt: String? = null,
    @SerialName("open_house_status") val openHouseStatus: String? = null,
    @SerialName("property_title") val propertyTitle: String? = null,
    @SerialName("property_address_line_1") val propertyAddressLine1: String? = null,
    @SerialName("property_city") val propertyCity: String? = null,
    @SerialName("property_region") val propertyRegion: String? = null
) {
    fun toListDomain(): Lead = Lead(
        id = id,
        visitorId = visitorId,
        openHouseId = openingId,
        propertyId = propertyId,
        fullName = contactFullName?.takeIf { it.isNotBlank() } ?: "Lead",
        email = contactEmail,
        phone = contactPhone,
        avatarDataUrl = havnPublicStorageSource(HavnMediaBucket.ContactPhotos, contactAvatarStoragePath ?: contactAvatarDataUrl),
        temperature = LeadTemperature.fromDatabase(temperature),
        intentScore = intentScore,
        notes = notes ?: visitorNotes,
        checkedInAt = visitorCheckedInAt
            ?: openingContactCheckedInAt
            ?: createdAt
            ?: updatedAt
            ?: openHouseStartsAt
            ?: "",
        createdAt = createdAt,
        updatedAt = updatedAt,
        sourceProperty = LeadPropertySource(
            id = propertyId,
            title = propertyTitle?.takeIf { it.isNotBlank() } ?: "Property",
            address = listOfNotNull(
                propertyAddressLine1?.takeIf { it.isNotBlank() },
                propertyCity?.takeIf { it.isNotBlank() },
                propertyRegion?.takeIf { it.isNotBlank() }
            ).joinToString(", ")
        ),
        sourceOpenHouse = LeadOpenHouseSource(
            id = openingId,
            title = openHouseTitle,
            startsAt = openHouseStartsAt ?: createdAt.orEmpty(),
            endsAt = openHouseEndsAt ?: openHouseStartsAt ?: createdAt.orEmpty(),
            status = openHouseStatus ?: "scheduled"
        )
    )
}

@Serializable
private data class LeadClientRow(
    val id: String,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    @SerialName("avatar_url") val avatarDataUrl: String? = null,
    @SerialName("avatar_storage_path") val avatarStoragePath: String? = null
) {
    val displayAvatarUrl: String?
        get() = havnPublicStorageSource(HavnMediaBucket.ContactPhotos, avatarStoragePath ?: avatarDataUrl)
}

@Serializable
private data class OpeningClientRow(
    val id: String,
    @SerialName("opening_id") val openingId: String = "",
    @SerialName("contact_id") val clientId: String = "",
    @SerialName("checked_in_at") val checkedInAt: String? = null
)

@Serializable
private data class VisitorRow(
    val id: String,
    val notes: String? = null,
    @SerialName("checked_in_at") val checkedInAt: String,
)

@Serializable
private data class LeadOpenHouseRow(
    val id: String,
    @SerialName("property_id") val propertyId: String,
    val title: String? = null,
    @SerialName("starts_at") val startsAt: String,
    @SerialName("ends_at") val endsAt: String,
    val status: String
) {
    fun toDomain(): LeadOpenHouseSource = LeadOpenHouseSource(
        id = id,
        title = title,
        startsAt = startsAt,
        endsAt = endsAt,
        status = status
    )
}

@Serializable
private data class LeadPropertyRow(
    val id: String,
    val title: String,
    @SerialName("address_line_1") val addressLine1: String,
    val city: String,
    val region: String
) {
    fun toDomain(): LeadPropertySource = LeadPropertySource(
        id = id,
        title = title,
        address = "$addressLine1, $city, $region"
    )
}

@Serializable
private data class LeadPropertyPhotoRow(
    val id: String,
    @SerialName("storage_path") val storagePath: String,
    @SerialName("thumbnail_url") val thumbnailDataUrl: String? = null,
    @SerialName("thumbnail_width") val thumbnailWidth: Int? = null,
    @SerialName("sort_order") val sortOrder: Int = 0,
    @SerialName("created_at") val createdAt: String? = null
) {
    val displayDataUrl: String?
        get() = havnDisplayPhotoSource(storagePath, thumbnailDataUrl, thumbnailWidth)
}

@Serializable
private data class LeadFollowUpRow(
    val id: String,
    @SerialName("visitor_id") val visitorId: String,
    val channel: String,
    val status: String,
    @SerialName("due_at") val dueAt: String? = null,
    @SerialName("completed_at") val completedAt: String? = null,
    val subject: String? = null,
    val body: String? = null,
    @SerialName("created_at") val createdAt: String? = null
) {
    fun toDomain(): LeadFollowUp = LeadFollowUp(
        id = id,
        channel = channel,
        status = status,
        dueAt = dueAt,
        completedAt = completedAt,
        subject = subject,
        body = body,
        createdAt = createdAt
    )
}

@Serializable
private data class SetLeadTemperatureParams(
    @SerialName("p_lead_id") val leadId: String,
    @SerialName("p_temperature") val temperature: String
)

@Serializable
private data class SearchPageParams(
    @SerialName("p_agency_id") val agencyId: String?,
    @SerialName("p_query") val query: String?,
    @SerialName("p_limit") val limit: Int,
    @SerialName("p_offset") val offset: Int
)

private data class SearchPageCacheKey(
    val agentId: String,
    val agencyId: String?,
    val query: String,
    val offset: Int,
    val limit: Int
)

private data class LeadPageCacheEntry(
    val savedAtMs: Long,
    val value: List<Lead>
)

@Serializable
private data class LeadNotesUpdateRow(val notes: String?)
